import styled from "@emotion/styled";
import { Button, Input, Select, Space } from "antd";
import { FC, SetStateAction, useEffect, useState } from "react";
import type { PartnerTransportBrand } from "services/interface";

interface SearchTrackFormProps {
  orderCode: string;
  setOrderCode: (value: SetStateAction<string>) => void;
  /** ค้นจากเลขพัสดุ — ส่งไปเป็น tracking_number ใน API */
  trackingNumber: string;
  setTrackingNumber: (value: SetStateAction<string>) => void;
  /** "" = ทั้งหมด, "true" = ปริ้นแล้ว, "false" = ยังไม่ปริ้น — ส่งไปเป็น is_print ใน API */
  isPrintFilter: string;
  setIsPrintFilter: (value: SetStateAction<string>) => void;
  /** ค้นจากชื่อ ref (ร้าน/ตัวแทน) — ส่งไปเป็น ref_name ใน API */
  refName: string;
  setRefName: (value: SetStateAction<string>) => void;
  /** ค้นจากเบอร์โทร ref — ส่งไปเป็น ref_telephone ใน API */
  refTelephone: string;
  setRefTelephone: (value: SetStateAction<string>) => void;
  onSearch: () => void;
  onClear: () => void;
  /** true = แท็บส่งกับแบรนด์ — โชว์ Select แบรนด์ (หลายตัวเลือก) หน้า input เลขพัสดุ */
  showBrandFilter?: boolean;
  /** รายการแบรนด์สำหรับ Select (เฉพาะเมื่อ showBrandFilter = true) */
  brandList?: PartnerTransportBrand[];
  /** ค่าแบรนด์ที่เลือก (brand_code หรือ id) — ส่งไป API เป็น brand_codes */
  selectedBrandCodes?: string[];
  setSelectedBrandCodes?: (value: string[] | ((prev: string[]) => string[])) => void;
}

const VALUE_ALL = "";

const IS_PRINT_OPTIONS = [
  { value: "", label: "ทั้งหมด" },
  { value: "true", label: "ปริ้นแล้ว" },
  { value: "false", label: "ยังไม่ปริ้น" },
] as const;

/** ค่า brand ที่ใช้เป็น value ใน Select (brand_code หรือ id) */
const getBrandValue = (b: PartnerTransportBrand) =>
  (b.brand_code != null && b.brand_code !== "") ? b.brand_code : b.id;

const SearchTrackForm: FC<SearchTrackFormProps> = ({
  orderCode,
  setOrderCode,
  trackingNumber,
  setTrackingNumber,
  isPrintFilter,
  setIsPrintFilter,
  refName,
  setRefName,
  refTelephone,
  setRefTelephone,
  onSearch,
  onClear,
  showBrandFilter = false,
  brandList = [],
  selectedBrandCodes = [],
  setSelectedBrandCodes,
}) => {
  const [tempOrderCode, setTempOrderCode] = useState<string>(orderCode);
  const [tempTrackingNumber, setTempTrackingNumber] = useState<string>(trackingNumber);
  const [tempIsPrintFilter, setTempIsPrintFilter] = useState<string>(isPrintFilter);
  const [tempRefName, setTempRefName] = useState<string>(refName);
  const [tempRefTelephone, setTempRefTelephone] = useState<string>(refTelephone);

  useEffect(() => {
    setTempOrderCode(orderCode);
  }, [orderCode]);

  useEffect(() => {
    setTempTrackingNumber(trackingNumber);
  }, [trackingNumber]);

  useEffect(() => {
    setTempIsPrintFilter(isPrintFilter);
  }, [isPrintFilter]);

  useEffect(() => {
    setTempRefName(refName);
  }, [refName]);

  useEffect(() => {
    setTempRefTelephone(refTelephone);
  }, [refTelephone]);

  const handleSearch = () => {
    setOrderCode(tempOrderCode);
    setTrackingNumber(tempTrackingNumber);
    setIsPrintFilter(tempIsPrintFilter);
    setRefName(tempRefName);
    setRefTelephone(tempRefTelephone);
    onSearch();
  };

  const handleClear = () => {
    setTempOrderCode("");
    setTempTrackingNumber("");
    setTempIsPrintFilter(VALUE_ALL);
    setTempRefName("");
    setTempRefTelephone("");
    setOrderCode("");
    setTrackingNumber("");
    setIsPrintFilter(VALUE_ALL);
    setRefName("");
    setRefTelephone("");
    if (setSelectedBrandCodes) setSelectedBrandCodes([]);
    onClear();
  };

  const brandOptions = brandList.map((b) => ({
    value: getBrandValue(b),
    label: b.brand_name,
  }));

  return (
    <ButtonWrapper>
      <Space.Compact style={{ flexWrap: "wrap", gap: 8 }}>
        {showBrandFilter && (
          <Select
            mode="multiple"
            style={{ minWidth: 200, height: "32px" }}
            placeholder="แบรนด์ (ทั้งหมด)"
            value={selectedBrandCodes}
            onChange={(v) => setSelectedBrandCodes?.(v ?? [])}
            options={brandOptions}
            allowClear
            maxTagCount="responsive"
          />
        )}
        <Input
          style={{ width: 160, height: "32px" }}
          placeholder="เลขพัสดุ"
          value={tempTrackingNumber}
          onChange={(e) => setTempTrackingNumber(e.target.value)}
        />
        <Input
          style={{ width: 180, height: "32px" }}
          placeholder="รหัสออเดอร์"
          value={tempOrderCode}
          onChange={(e) => setTempOrderCode(e.target.value)}
        />
        <Input
          style={{ width: 160, height: "32px" }}
          placeholder="ชื่อผู้ติดต่อ"
          value={tempRefName}
          onChange={(e) => setTempRefName(e.target.value)}
          // allowClear
        />
        <Input
          style={{ width: 140, height: "32px" }}
          placeholder="เบอร์โทรผู้ติดต่อ"
          value={tempRefTelephone}
          onChange={(e) => setTempRefTelephone(e.target.value)}
          // allowClear
        />
        <Select
          style={{ width: 140, height: "32px" }}
          placeholder="สถานะพิมพ์"
          value={tempIsPrintFilter}
          onChange={(v) => setTempIsPrintFilter(v ?? VALUE_ALL)}
          options={IS_PRINT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          allowClear={false}
        />
        <Button type="primary" onClick={handleSearch}>
          ค้นหา
        </Button>
        {(tempOrderCode || tempTrackingNumber.trim() !== "" || tempIsPrintFilter !== VALUE_ALL || tempRefName.trim() !== "" || tempRefTelephone.trim() !== "" || (showBrandFilter && selectedBrandCodes.length > 0)) && (
          <Button type="default" onClick={handleClear}>
            ล้าง
          </Button>
        )}
      </Space.Compact>
    </ButtonWrapper>
  );
};

export default SearchTrackForm;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;
